import type { AtsCandidateListItem } from '../types';

export type AtsFolderNode = {
  id: string;
  name: string;
  path: string;
  count: number;
  children: AtsFolderNode[];
};

export type AtsFolderLevel = 'country' | 'project' | 'vacancy';

export const ALL_COUNTRIES_FOLDER = 'Все страны';
export const DEFAULT_COUNTRY_FOLDER = 'Без страны';
const DEFAULT_PROJECT_FOLDER = 'Без проекта';
const DEFAULT_VACANCY_FOLDER = 'Без вакансии';

const REGION_ALIASES: Record<string, string> = {
  apac: 'APAC',
  emea: 'EMEA',
  amer: 'AMER',
  latam: 'LATAM',
  mena: 'MENA',
  ceemea: 'CEEMEA',
  africa: 'AFRICA',
  europe: 'EUROPE',
};

const COUNTRY_FOLDERS = new Set([
  'без страны',
  'россия', 'russia', 'беларусь', 'belarus', 'казахстан', 'kazakhstan',
  'германия', 'germany', 'польша', 'poland', 'сша', 'usa', 'united states',
  'канада', 'canada', 'китай', 'china', 'индия', 'india', 'оаэ', 'uae',
  'великобритания', 'united kingdom', 'uk', 'франция', 'france', 'испания', 'spain',
  'италия', 'italy', 'турция', 'turkey', 'израиль', 'israel', 'египет', 'egypt',
  'бразилия', 'brazil', 'мексика', 'mexico', 'аргентина', 'argentina',
  'япония', 'japan', 'сингапур', 'singapore', 'австралия', 'australia',
  'южная корея', 'south korea', 'корея', 'korea', 'таиланд', 'thailand',
  'вьетнам', 'vietnam', 'индонезия', 'indonesia', 'малайзия', 'malaysia',
  'филиппины', 'philippines', 'нидерланды', 'netherlands', 'швеция', 'sweden',
  'норвегия', 'norway', 'финляндия', 'finland', 'чехия', 'czech republic',
  'румыния', 'romania', 'украина', 'ukraine', 'грузия', 'georgia',
  'армения', 'armenia', 'азербайджан', 'azerbaijan', 'узбекистан', 'uzbekistan',
]);

const GEO_TOKEN_RE = /[A-Za-z]{3,}|[А-Яа-яЁё]{3,}/g;

export const geoFromLabel = (value: string): string | null => {
  const stripped = value.trim();
  if (!stripped) return null;
  const lowered = stripped.toLocaleLowerCase();
  if (lowered === DEFAULT_COUNTRY_FOLDER.toLocaleLowerCase() || lowered === ALL_COUNTRIES_FOLDER.toLocaleLowerCase()) {
    return DEFAULT_COUNTRY_FOLDER;
  }
  if (REGION_ALIASES[lowered]) return REGION_ALIASES[lowered];
  if (COUNTRY_FOLDERS.has(lowered)) return stripped;
  const tokens = stripped.match(GEO_TOKEN_RE) || [];
  for (const token of tokens) {
    const mapped = REGION_ALIASES[token.toLocaleLowerCase()];
    if (mapped) return mapped;
  }
  return null;
};

export const resolveArchiveFolderPath = (parts: string[]): string => {
  const cleaned = parts.map((part) => part.trim()).filter(Boolean);
  if (cleaned.length === 0) {
    return `${DEFAULT_COUNTRY_FOLDER}/${DEFAULT_PROJECT_FOLDER}/${DEFAULT_VACANCY_FOLDER}`;
  }
  for (let index = 0; index < cleaned.length; index += 1) {
    const geo = geoFromLabel(cleaned[index]);
    if (geo) {
      return padFolderParts([geo, ...cleaned.slice(index + 1)]);
    }
  }
  if (cleaned.length === 1) {
    return padFolderParts([DEFAULT_COUNTRY_FOLDER, DEFAULT_PROJECT_FOLDER, cleaned[0]]);
  }
  let rest = cleaned.slice(1);
  if (rest.length === 1) {
    return padFolderParts([DEFAULT_COUNTRY_FOLDER, DEFAULT_PROJECT_FOLDER, rest[0]]);
  }
  if (rest.length > 2) {
    rest = rest.slice(-2);
  }
  return padFolderParts([DEFAULT_COUNTRY_FOLDER, ...rest]);
};

const padFolderParts = (parts: string[]): string => {
  if (parts.length === 1) return `${parts[0]}/${DEFAULT_PROJECT_FOLDER}/${DEFAULT_VACANCY_FOLDER}`;
  if (parts.length === 2) return `${parts[0]}/${parts[1]}/${DEFAULT_VACANCY_FOLDER}`;
  if (parts.length > 3) return parts.slice(-3).join('/');
  return parts.join('/');
};

export const isAllCountriesFolderName = (value: string | null | undefined): boolean => (
  Boolean(value && value.trim() === ALL_COUNTRIES_FOLDER)
);

/** Real DB folder created by the old import bug; remap so it does not collide with the synthetic root. */
export const remapReservedFolderPath = (path: string): string => {
  const parts = normalizePath(path).split('/').filter(Boolean);
  if (parts[0] === ALL_COUNTRIES_FOLDER) {
    parts[0] = DEFAULT_COUNTRY_FOLDER;
  }
  return parts.join('/');
};

export const getFolderCreateParentPath = (
  level: AtsFolderLevel,
  country: string,
  project: string,
): string | null => {
  if (level === 'country') return null;
  if (level === 'project') return country.trim() || null;
  const normalizedCountry = country.trim();
  const normalizedProject = project.trim();
  return normalizedCountry && normalizedProject ? `${normalizedCountry}/${normalizedProject}` : null;
};

const getRecordString = (value: unknown, key: string): string | null => {
  if (typeof value !== 'object' || value === null || !(key in value)) {
    return null;
  }

  const field = (value as Record<string, unknown>)[key];
  return typeof field === 'string' && field.trim() ? field.trim() : null;
};

const normalizePath = (value: string): string => value
  .split('/')
  .map((part) => part.trim())
  .filter(Boolean)
  .join('/');

const parentPath = (sourcePath: string): string | null => {
  const parts = normalizePath(sourcePath).split('/').filter(Boolean);
  if (parts.length <= 1) return null;
  return parts.slice(0, -1).join('/');
};

const getDocumentFolderPaths = (item: Partial<AtsCandidateListItem>): string[] => {
  const paths = new Set<string>();

  (item.documents || []).forEach((document) => {
    const folderPath = getRecordString(document, 'folder_path');
    const sourcePath = getRecordString(document, 'source_path');
    const path = folderPath || (sourcePath ? parentPath(sourcePath) : null);
    if (path) {
      paths.add(normalizePath(path));
    }
  });

  return Array.from(paths);
};

const getRawFolderPaths = (item: Partial<AtsCandidateListItem>): string[] => {
  const paths = getDocumentFolderPaths(item);
  return paths.length ? paths : [normalizePath(item.desired_position || item.source || item.location_city || 'Без папки')];
};

const getCommonRoot = (paths: string[]): string | null => {
  const parts = paths
    .map((path) => path.split('/').filter(Boolean))
    .filter((pathParts) => pathParts.length === 2);

  if (parts.length === 0 || parts.length !== paths.length) return null;

  const root = parts[0][0];
  return parts.every((pathParts) => pathParts[0] === root) ? root : null;
};

const stripCommonRoot = (path: string, commonRoot: string | null): string => {
  if (!commonRoot) return path;
  const parts = path.split('/').filter(Boolean);
  return parts[0] === commonRoot && parts.length > 1 ? parts.slice(1).join('/') : path;
};

const toStructuredFolderPath = (path: string): string => {
  const parts = remapReservedFolderPath(normalizePath(path)).split('/').filter(Boolean);
  // Three levels returned by the ATS API are already country/project/vacancy.
  // A recruiter can create any country name; archive geo inference must not
  // rewrite that persisted path into «Без страны».
  if (parts.length === 3) return parts.join('/');
  return resolveArchiveFolderPath(parts);
};

const getDisplayFolderPaths = (
  item: Partial<AtsCandidateListItem>,
): string[] => getRawFolderPaths(item)
  .map((path) => toStructuredFolderPath(remapReservedFolderPath(path)));

const getCandidateFolderPathVariants = (
  item: Partial<AtsCandidateListItem>,
  allItems: Partial<AtsCandidateListItem>[] = [],
): string[] => {
  const allPaths = allItems.flatMap(getRawFolderPaths);
  const commonRoot = getCommonRoot(allPaths);
  const variants = new Set<string>();

  getRawFolderPaths(item).forEach((path) => {
    const remapped = remapReservedFolderPath(path);
    const stripped = stripCommonRoot(path, commonRoot);
    const remappedStripped = remapReservedFolderPath(stripped);
    variants.add(path);
    variants.add(remapped);
    variants.add(stripped);
    variants.add(remappedStripped);
    variants.add(toStructuredFolderPath(remapped));
    variants.add(toStructuredFolderPath(remappedStripped));
  });

  return Array.from(variants).filter(Boolean);
};

export const getCandidateFolderPaths = (
  item: Partial<AtsCandidateListItem>,
  _allItems: Partial<AtsCandidateListItem>[] = [],
): string[] => getDisplayFolderPaths(item);

/** Actual document folders, without deriving a folder from a job title or location. */
export const getCandidateDocumentFolderPaths = (item: Partial<AtsCandidateListItem>): string[] => (
  Array.from(new Set(getDocumentFolderPaths(item).map(toStructuredFolderPath)))
);

export const getFolderVacancyTitle = (folderPath: string): string => {
  const parts = folderPath.split('/').filter(Boolean);
  return parts[parts.length - 1] || folderPath.trim();
};

export const getPrimaryFolderName = (
  item: Partial<AtsCandidateListItem>,
  allItems: Partial<AtsCandidateListItem>[] = [],
): string => {
  const firstPath = getCandidateFolderPathVariants(item, allItems)[0] || 'Без папки';
  return getFolderVacancyTitle(firstPath) || firstPath;
};

export const candidateBelongsToFolder = (
  item: Partial<AtsCandidateListItem>,
  folderPath: string,
  allItems: Partial<AtsCandidateListItem>[],
): boolean => {
  const normalizedFolder = remapReservedFolderPath(folderPath);
  if (normalizedFolder === ALL_COUNTRIES_FOLDER || folderPath === ALL_COUNTRIES_FOLDER) {
    return true;
  }
  return getCandidateFolderPathVariants(item, allItems).some((path) => {
    const remappedPath = remapReservedFolderPath(path);
    return (
      remappedPath === normalizedFolder
      || remappedPath.startsWith(`${normalizedFolder}/`)
      || path === normalizedFolder
      || path.startsWith(`${normalizedFolder}/`)
    );
  });
};

export const folderPathMatches = (
  folderPath: string,
  selectedFolderPath: string,
  allItems: Partial<AtsCandidateListItem>[],
): boolean => {
  const normalizedFolder = normalizePath(folderPath);
  const normalizedSelected = normalizePath(selectedFolderPath);
  const commonRoot = getCommonRoot(allItems.flatMap(getRawFolderPaths));
  const displayFolder = stripCommonRoot(normalizedFolder, commonRoot);
  const displaySelected = stripCommonRoot(normalizedSelected, commonRoot);

  return (
    normalizedFolder === normalizedSelected
    || displayFolder === normalizedSelected
    || normalizedFolder === displaySelected
    || displayFolder === displaySelected
  );
};

export const buildFolderTree = (
  items: AtsCandidateListItem[],
  savedFolderPaths: string[] = [],
): AtsFolderNode[] => {
  const nodes = new Map<string, AtsFolderNode>();
  const rootNodes: AtsFolderNode[] = [];

  items.forEach((item) => {
    const countedPaths = new Set<string>();

    getDisplayFolderPaths(item)
      .forEach((path) => {
      const parts = path.split('/').filter(Boolean);
      let currentPath = '';
      let siblings = rootNodes;

      parts.forEach((part) => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        let node = nodes.get(currentPath);

        if (!node) {
          node = {
            id: currentPath,
            name: part,
            path: currentPath,
            count: 0,
            children: [],
          };
          nodes.set(currentPath, node);
          siblings.push(node);
          siblings.sort((left, right) => left.name.localeCompare(right.name, 'ru'));
        }

        if (!countedPaths.has(currentPath)) {
          node.count += 1;
          countedPaths.add(currentPath);
        }

        siblings = node.children;
      });
    });
  });

  savedFolderPaths.forEach((savedPath) => {
    const parts = remapReservedFolderPath(normalizePath(savedPath)).split('/').filter(Boolean);
    let currentPath = '';
    let siblings = rootNodes;

    parts.forEach((part) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      let node = nodes.get(currentPath);

      if (!node) {
        node = {
          id: currentPath,
          name: part,
          path: currentPath,
          count: 0,
          children: [],
        };
        nodes.set(currentPath, node);
        siblings.push(node);
        siblings.sort((left, right) => left.name.localeCompare(right.name, 'ru'));
      }

      siblings = node.children;
    });
  });

  return rootNodes;
};

export const buildFolderTreeFromPaths = (
  savedFolderPaths: string[],
  counts: Record<string, number> = {},
): AtsFolderNode[] => {
  const applyCounts = (nodes: AtsFolderNode[]): AtsFolderNode[] => nodes.map((node) => ({
    ...node,
    count: counts[node.path] ?? 0,
    children: applyCounts(node.children),
  }));

  return applyCounts(buildFolderTree([], savedFolderPaths));
};

/** Keep matching paths and their ancestors without changing the source catalog. */
export const filterFolderTree = (nodes: AtsFolderNode[], query: string): AtsFolderNode[] => {
  const normalized = query.trim().toLocaleLowerCase('ru-RU');
  if (!normalized) return nodes;
  const filterNodes = (items: AtsFolderNode[]): AtsFolderNode[] => items.flatMap((node) => {
    // A matching parent keeps its descendants, just like the move dialog.
    if (node.path.toLocaleLowerCase('ru-RU').includes(normalized)) return [node];
    const children = filterNodes(node.children);
    return children.length ? [{ ...node, children }] : [];
  });
  return filterNodes(nodes);
};
